import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DragEndEvent, DraggableAttributes, DraggableSyntheticListeners,DragStartEvent } from '@dnd-kit/core';
import { DndContext, DragOverlay, MouseSensor, TouchSensor, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { DragIndicator, ExpandMore as ExpandMoreIcon, InfoOutlined } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Radio,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';

import type {
  CoverageSelections,
  PatientCoverageSummary,
  PatientSummaryWithCoverages,
} from '../../types/merge-customization';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SLOT_LABELS: Record<number, string> = {
  1: 'Primary Coverage',
  2: 'Secondary Coverage',
  3: 'Tertiary Coverage',
};

const MAX_SLOTS = 3;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CoverageEntry {
  coverageId: string;
  patientId: string;
  patientName: string;
  coverage: PatientCoverageSummary;
}

interface SlotState {
  order: number;
  label: string;
  entries: CoverageEntry[];
  /** patient_insurance_coverage_id of the selected entry, or null */
  selectedId: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCoverageTitle(cov: PatientCoverageSummary): string {
  const parts: string[] = [];
  if (cov.payer_name) parts.push(cov.payer_name);
  if (cov.plan_name) parts.push(cov.plan_name);
  if (parts.length === 0 && cov.member_id) parts.push(cov.member_id);
  return parts.join(' — ') || 'Coverage';
}

function formatCoverageDetails(cov: PatientCoverageSummary): string | null {
  const parts: string[] = [];
  if (cov.member_id) parts.push(`ID: ${cov.member_id}`);
  if (cov.group_number) parts.push(`Group: ${cov.group_number}`);
  if (cov.effective_date) {
    const eff = new Date(cov.effective_date).toLocaleDateString();
    if (cov.termination_date) {
      parts.push(`${eff} – ${new Date(cov.termination_date).toLocaleDateString()}`);
    } else {
      parts.push(`Eff: ${eff}`);
    }
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * Pick the entry with the most recent updated_at.
 * Null updated_at is treated as epoch 0 (oldest).
 */
function pickDefaultEntry(entries: CoverageEntry[]): string {
  let best = entries[0];
  let bestTime = best.coverage.updated_at ? new Date(best.coverage.updated_at).getTime() : 0;

  for (let i = 1; i < entries.length; i++) {
    const updatedAt = entries[i].coverage.updated_at;
    const t = updatedAt ? new Date(updatedAt).getTime() : 0;
    if (t > bestTime) {
      best = entries[i];
      bestTime = t;
    }
  }

  return best.coverageId;
}

/** Build initial slot state from patient data. Always shows slots 1–3. */
function initializeSlots(patients: PatientSummaryWithCoverages[]): SlotState[] {
  const entryMap = new Map<number, CoverageEntry[]>();

  for (const patient of patients) {
    if (!patient.coverages) continue;
    const patientName =
      [patient.first_name, patient.last_name].filter(Boolean).join(' ') ||
      patient.patient_id.substring(0, 8);

    for (const cov of patient.coverages) {
      const entries = entryMap.get(cov.coverage_order) ?? [];
      entries.push({
        coverageId: cov.patient_insurance_coverage_id,
        patientId: patient.patient_id,
        patientName,
        coverage: cov,
      });
      entryMap.set(cov.coverage_order, entries);
    }
  }

  // Determine how many slots to show: at least up to the highest occupied, capped at MAX_SLOTS
  const maxOccupied = entryMap.size > 0 ? Math.max(...entryMap.keys()) : 0;
  const slotCount = Math.min(Math.max(maxOccupied, 1), MAX_SLOTS);

  const slots: SlotState[] = [];
  for (let order = 1; order <= slotCount; order++) {
    const entries = entryMap.get(order) ?? [];
    slots.push({
      order,
      label: SLOT_LABELS[order] ?? `Coverage ${order}`,
      entries,
      selectedId: entries.length === 1
        ? entries[0].coverageId
        : entries.length > 1
          ? pickDefaultEntry(entries)
          : null,
    });
  }

  return slots;
}

/** Build the parent-facing selections map from current slot state. */
function buildSelections(slots: SlotState[]): CoverageSelections {
  const result: CoverageSelections = {};
  for (const slot of slots) {
    if (slot.selectedId) {
      result[String(slot.order)] = slot.selectedId;
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CoverageSlotSelectorProps {
  patients: PatientSummaryWithCoverages[];
  onSelectionsChange: (selections: CoverageSelections) => void;
  patientColors: Map<string, string>;
  expanded?: boolean;
  onToggle?: () => void;
  isLoading?: boolean;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function CoverageSlotSelector({
  patients,
  onSelectionsChange,
  patientColors,
  expanded,
  onToggle,
  isLoading = false,
}: CoverageSlotSelectorProps) {
  const hasCoverages = patients.some(p => p.coverages && p.coverages.length > 0);
  const [slots, setSlots] = useState<SlotState[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const slotsInitializedRef = useRef(false);

  // Initialize slots only after coverages have loaded
  useEffect(() => {
    if (isLoading || slotsInitializedRef.current) return;
    slotsInitializedRef.current = true;
    setSlots(initializeSlots(patients));
  }, [isLoading, patients]);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 8 },
    }),
  );

  // Report selections to parent whenever slots change
  const onSelectionsChangeRef = useRef(onSelectionsChange);
  onSelectionsChangeRef.current = onSelectionsChange;
  useEffect(() => {
    onSelectionsChangeRef.current(buildSelections(slots));
  }, [slots]);

  const conflictCount = useMemo(
    () => slots.filter(s => s.entries.length > 1).length,
    [slots],
  );

  // Find the active entry for the drag overlay
  const activeEntry = useMemo(() => {
    if (!activeId) return null;
    for (const slot of slots) {
      const entry = slot.entries.find(e => e.coverageId === activeId);
      if (entry) return entry;
    }
    return null;
  }, [activeId, slots]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const targetSlotOrder = over.data.current?.slotOrder as number | undefined;
    if (targetSlotOrder == null) return;

    const covId = String(active.id);

    setSlots(prev => {
      const sourceSlot = prev.find(s => s.entries.some(e => e.coverageId === covId));
      if (!sourceSlot || sourceSlot.order === targetSlotOrder) return prev;

      const entry = sourceSlot.entries.find(e => e.coverageId === covId);
      if (!entry) return prev;

      return prev.map(slot => {
        if (slot.order === sourceSlot.order) {
          const newEntries = slot.entries.filter(e => e.coverageId !== covId);
          return {
            ...slot,
            entries: newEntries,
            selectedId: slot.selectedId === covId
              ? (newEntries.length > 0 ? pickDefaultEntry(newEntries) : null)
              : slot.selectedId,
          };
        }
        if (slot.order === targetSlotOrder) {
          return {
            ...slot,
            entries: [...slot.entries, entry],
            selectedId: covId,
          };
        }
        return slot;
      });
    });
  }, []);

  const handleRadioChange = useCallback((slotOrder: number, coverageId: string) => {
    setSlots(prev =>
      prev.map(slot =>
        slot.order === slotOrder ? { ...slot, selectedId: coverageId } : slot,
      ),
    );
  }, []);

  if (!isLoading && !hasCoverages) return null;

  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      disableGutters
      sx={{ '&:before': { display: 'none' } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: 'grey.50',
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: 48,
          '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 },
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          Coverages
        </Typography>
        {isLoading ? (
          <Skeleton variant="rounded" width={90} height={22} />
        ) : (
          <Chip
            label={conflictCount > 0 ? `${conflictCount} conflict${conflictCount !== 1 ? 's' : ''}` : 'No conflicts'}
            size="small"
            color={conflictCount > 0 ? 'info' : 'success'}
            variant="outlined"
            sx={{ height: 22, fontSize: '0.75rem' }}
          />
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        {isLoading ? (
          <Box sx={{ px: 1.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[1, 2, 3].map(i => (
              <Skeleton key={i} variant="rounded" height={48} />
            ))}
          </Box>
        ) : (
        <>
        {/* Instruction banner */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: 'info.50',
          }}
        >
          <InfoOutlined sx={{ fontSize: 16, color: 'info.main' }} />
          <Typography variant="caption" color="text.secondary">
            Drag coverages between slots to change priority, or select which to keep when duplicates exist.
          </Typography>
        </Box>

        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {slots.map(slot => (
            <SlotDropZone
              key={slot.order}
              slot={slot}
              selectedId={slot.selectedId}
              onRadioChange={handleRadioChange}
              patientColors={patientColors}
              isDragActive={activeId != null}
            />
          ))}
          <DragOverlay dropAnimation={null}>
            {activeEntry ? (
              <CoverageCardContent
                entry={activeEntry}
                patientColor={patientColors.get(activeEntry.patientId) ?? '#888'}
                isOverlay
              />
            ) : null}
          </DragOverlay>
        </DndContext>
        </>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

// ---------------------------------------------------------------------------
// Slot drop zone
// ---------------------------------------------------------------------------

interface SlotDropZoneProps {
  slot: SlotState;
  selectedId: string | null;
  onRadioChange: (slotOrder: number, coverageId: string) => void;
  patientColors: Map<string, string>;
  isDragActive: boolean;
}

function SlotDropZone({ slot, selectedId, onRadioChange, patientColors, isDragActive }: SlotDropZoneProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `slot-${slot.order}`,
    data: { slotOrder: slot.order },
  });

  const showRadios = slot.entries.length > 1;
  const isEmpty = slot.entries.length === 0;

  return (
    <Box
      ref={setNodeRef}
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-child': { borderBottom: 0 },
        backgroundColor: isOver ? 'action.hover' : 'transparent',
        transition: 'background-color 0.15s ease',
      }}
    >
      <Typography
        variant="caption"
        fontWeight={600}
        sx={{ px: 1.5, pt: 1, pb: 0.5, display: 'block', color: 'text.secondary' }}
      >
        {slot.label}
      </Typography>
      <Box sx={{ px: 0.5, pb: 0.5, minHeight: 32 }}>
        {isEmpty ? (
          <Box
            sx={{
              mx: 1,
              my: 0.5,
              py: 1.5,
              px: 2,
              border: '2px dashed',
              borderColor: isDragActive
                ? isOver ? 'primary.main' : 'primary.light'
                : 'grey.300',
              borderRadius: 1,
              backgroundColor: isDragActive
                ? isOver ? 'primary.50' : 'grey.50'
                : 'grey.50',
              textAlign: 'center',
              transition: 'border-color 0.15s ease, background-color 0.15s ease',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Drag a coverage here to assign as {slot.label.toLowerCase()}
            </Typography>
          </Box>
        ) : (
          slot.entries.map(entry => (
            <DraggableCoverageCard
              key={entry.coverageId}
              entry={entry}
              slotOrder={slot.order}
              isSelected={entry.coverageId === selectedId}
              showRadio={showRadios}
              onRadioChange={() => onRadioChange(slot.order, entry.coverageId)}
              patientColor={patientColors.get(entry.patientId) ?? '#888'}
            />
          ))
        )}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Draggable coverage card
// ---------------------------------------------------------------------------

interface DraggableCoverageCardProps {
  entry: CoverageEntry;
  slotOrder: number;
  isSelected: boolean;
  showRadio: boolean;
  onRadioChange: () => void;
  patientColor: string;
}

function DraggableCoverageCard({
  entry,
  slotOrder,
  isSelected,
  showRadio,
  onRadioChange,
  patientColor,
}: DraggableCoverageCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: entry.coverageId,
    data: { slotOrder },
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        opacity: isDragging ? 0.35 : 1,
        transition: 'opacity 0.15s ease',
      }}
    >
      <CoverageCardContent
        entry={entry}
        patientColor={patientColor}
        isSelected={isSelected}
        showRadio={showRadio}
        onRadioChange={onRadioChange}
        dragListeners={listeners}
        dragAttributes={attributes}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Card content (shared between in-place card and drag overlay)
// ---------------------------------------------------------------------------

interface CoverageCardContentProps {
  entry: CoverageEntry;
  patientColor: string;
  isSelected?: boolean;
  showRadio?: boolean;
  onRadioChange?: () => void;
  dragListeners?: DraggableSyntheticListeners;
  dragAttributes?: DraggableAttributes;
  isOverlay?: boolean;
}

function CoverageCardContent({
  entry,
  patientColor,
  isSelected = false,
  showRadio = false,
  onRadioChange,
  dragListeners,
  dragAttributes,
  isOverlay = false,
}: CoverageCardContentProps) {
  const { coverage, patientName } = entry;
  const title = formatCoverageTitle(coverage);
  const details = formatCoverageDetails(coverage);
  const isActive = coverage.status === 'active';

  return (
    <Box
      onClick={showRadio && onRadioChange ? onRadioChange : undefined}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.5,
        py: 0.75,
        px: 0.5,
        borderRadius: 1,
        cursor: showRadio ? 'pointer' : 'default',
        backgroundColor: isOverlay ? 'background.paper' : 'transparent',
        boxShadow: isOverlay ? 4 : 0,
        border: isOverlay ? '1px solid' : 'none',
        borderColor: 'divider',
        '&:hover': showRadio ? { backgroundColor: 'grey.50' } : {},
      }}
    >
      {/* Drag handle */}
      <Tooltip title="Drag to reorder" placement="left" enterDelay={400}>
        <Box
          {...dragListeners}
          {...dragAttributes}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'grab',
            color: 'text.secondary',
            mt: 0.25,
            '&:hover': { color: 'primary.main' },
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <DragIndicator fontSize="small" />
        </Box>
      </Tooltip>

      {/* Radio (only when multiple in slot) */}
      {showRadio ? (
        <Radio
          size="small"
          checked={isSelected}
          onChange={onRadioChange}
          sx={{ p: 0.25, mt: 0.125, color: patientColor }}
          inputProps={{
            'aria-label': `Select ${title} from ${patientName}`,
          }}
        />
      ) : null}

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Line 1: title + chips */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
            {title}
          </Typography>
          <Chip
            label={isActive ? 'Active' : coverage.status}
            size="small"
            color={isActive ? 'success' : 'default'}
            variant="outlined"
            sx={{ height: 18, fontSize: '0.65rem', textTransform: 'capitalize' }}
          />
          <Chip
            label={patientName}
            size="small"
            variant="outlined"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              borderColor: patientColor,
              color: patientColor,
            }}
          />
        </Box>
        {/* Line 2: details */}
        {details && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {details}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
